/**
 * Client-side merge-tag utilities (mirrors the server-side version).
 * Used for live drift warnings in the template editor.
 */

export const AUTO_START = "<!-- TDM:AUTO-FIELDS:START -->";
export const AUTO_END = "<!-- TDM:AUTO-FIELDS:END -->";

export interface TagField {
  key: string;
  label: string;
}

export function parseTags(str: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  let m: RegExpExecArray | null;
  const re = /\{\{\s*([\w.]+)\s*\}\}/g;
  while ((m = re.exec(str)) !== null) {
    const k = m[1]!;
    if (!seen.has(k)) { seen.add(k); out.push(k); }
  }
  return out;
}

export function extractAutoRegion(body: string): {
  before: string;
  auto: string;
  after: string;
} {
  const startIdx = body.indexOf(AUTO_START);
  const endIdx = body.indexOf(AUTO_END);
  if (startIdx === -1 || endIdx === -1 || endIdx < startIdx) {
    return { before: body, auto: "", after: "" };
  }
  return {
    before: body.slice(0, startIdx),
    auto: body.slice(startIdx + AUTO_START.length, endIdx),
    after: body.slice(endIdx + AUTO_END.length),
  };
}

export interface DriftWarning {
  type: "unused_field" | "orphaned_tag";
  key: string;
  label?: string;
  message: string;
}

const SYSTEM_TAGS =
  /^(accountName|opportunityName|mrr|tcv|closeDate|primaryContactName|supplier\.|sender\.|daysSinceSent|priorFollowUps|originalSubject|rep\.|deal\.|registration\.)/;

/**
 * Compute drift warnings.
 *
 * - "unused_field": field not referenced anywhere in subject or full body
 *   (including auto region). After a save all fields are auto-inserted, so
 *   this warns about fields added since the last save.
 * - "orphaned_tag": {{tag}} in manual subject/body has no matching field.
 */
export function computeDrift(
  subject: string,
  body: string,
  fields: TagField[],
): DriftWarning[] {
  const warnings: DriftWarning[] = [];
  const fieldKeys = new Set(fields.map((f) => f.key));

  // Field-used check: look at full body (including auto region)
  const usedInSubject = new Set(parseTags(subject));
  const usedInBody = new Set(parseTags(body));
  const allUsed = new Set([...usedInSubject, ...usedInBody]);

  for (const f of fields) {
    if (!allUsed.has(f.key)) {
      warnings.push({
        type: "unused_field",
        key: f.key,
        label: f.label,
        message: `Field "${f.label || f.key}" ({{${f.key}}}) is defined but not used in the email.`,
      });
    }
  }

  // Orphaned-tag check: look at manual content only (outside auto region)
  const { before, after } = extractAutoRegion(body);
  const manualBody = before + after;
  const usedInManualBody = new Set(parseTags(manualBody));
  const allManual = new Set([...usedInSubject, ...usedInManualBody]);

  for (const tag of allManual) {
    if (!fieldKeys.has(tag) && !SYSTEM_TAGS.test(tag)) {
      warnings.push({
        type: "orphaned_tag",
        key: tag,
        message: `Tag {{${tag}}} is used in the email but no matching field exists.`,
      });
    }
  }

  return warnings;
}

export function checkHasDrift(
  subject: string,
  body: string,
  fields: TagField[],
): boolean {
  return computeDrift(subject, body, fields).length > 0;
}
